import React, { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { BDAY_EASE } from '../theme';
import type { BirthdayContent } from '../content';

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

function getTimeLeft(targetIso: string): TimeLeft | null {
  const diff = new Date(targetIso).getTime() - Date.now();
  if (diff <= 0) return null;
  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((diff / (1000 * 60)) % 60),
    seconds: Math.floor((diff / 1000) % 60),
  };
}

function Digit({ value, label }: { value: number; label: string }) {
  return (
    <motion.div
      className="flex flex-col items-center gap-2"
      initial={{ opacity: 0, scale: 0.8 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, ease: BDAY_EASE }}
    >
      <div className="relative w-20 h-20 sm:w-24 sm:h-24 rounded-2xl bg-white/8 border border-blue-400/25 flex items-center justify-center backdrop-blur-sm shadow-lg overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-400/5 to-blue-900/20" />
        <motion.span
          key={value}
          initial={{ y: -12, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 12, opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="relative text-4xl sm:text-5xl font-extrabold text-white tabular-nums"
        >
          {String(value).padStart(2, '0')}
        </motion.span>
      </div>
      <p className="text-blue-400 text-[11px] font-bold uppercase tracking-widest">{label}</p>
    </motion.div>
  );
}

export function CountdownSection({ content }: { content: BirthdayContent }) {
  const [timeLeft, setTimeLeft] = useState<TimeLeft | null>(null);

  useEffect(() => {
    if (!content.eventIso) return;
    const initial = getTimeLeft(content.eventIso);
    setTimeLeft(initial);
    if (!initial) return;

    const id = setInterval(() => {
      const t = getTimeLeft(content.eventIso!);
      if (!t) { clearInterval(id); setTimeLeft(null); return; }
      setTimeLeft(t);
    }, 1000);

    return () => clearInterval(id);
  }, [content.eventIso]);

  // Hide if no target date or event already started
  if (!content.eventIso || timeLeft === null) return null;

  return (
    <section
      className="relative py-24 px-6 overflow-hidden text-center"
      style={{ background: 'linear-gradient(180deg, #1e3a5f 0%, #0f172a 100%)' }}
    >
      {/* glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 rounded-full bg-blue-600/15 blur-3xl pointer-events-none" />

      <div className="relative max-w-2xl mx-auto">
        {/* heading */}
        <motion.p
          initial={{ opacity: 0, y: -10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, ease: BDAY_EASE }}
          className="text-blue-400 text-xs font-bold uppercase tracking-[0.25em] mb-2"
        >
          🎂 Counting Down
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1, ease: BDAY_EASE }}
          className="text-3xl sm:text-4xl font-bold text-white mb-10"
        >
          The Celebration Begins In
        </motion.h2>

        {/* digits */}
        <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6">
          <Digit value={timeLeft.days} label="Days" />
          <span className="text-blue-400 text-3xl font-bold pb-8">:</span>
          <Digit value={timeLeft.hours} label="Hours" />
          <span className="text-blue-400 text-3xl font-bold pb-8">:</span>
          <Digit value={timeLeft.minutes} label="Minutes" />
          <span className="text-blue-400 text-3xl font-bold pb-8">:</span>
          <Digit value={timeLeft.seconds} label="Seconds" />
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-8 text-blue-300 text-sm"
        >
          Mark your calendars — it's going to be epic! 🎉
        </motion.p>
      </div>
    </section>
  );
}
